import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * ISPFeatureItem component
 * Displays an expandable ISP feature item
 */
const ISPFeatureItem = ({ feature, isExpanded, toggleFeature }) => {
  return (
    <div
      key={feature.id}
      className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden"
    >
      {/* Feature Header - Always visible */}
      <div
        className="p-6 flex items-center justify-between cursor-pointer"
        onClick={() => toggleFeature(feature.id)}
      >
        <div className="flex items-center">
          {feature.icon}
          <h4 className="text-xl font-semibold text-white ml-3">{feature.title}</h4>
        </div>
        <div className="flex items-center">
          <span className="text-gray-300 mr-3 hidden md:block">{feature.shortDescription}</span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Feature Details - Expandable */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 pt-2 border-t border-gray-700/50">
              <p className="text-gray-300">{feature.fullDescription}</p>
              
              {/* Mobile-only short description */}
              <p className="text-gray-400 text-sm mt-4 md:hidden">{feature.shortDescription}</p>
              
              {/* Feature-specific additional content */}
              {feature.id === 'uncapped' && (
                <div className="mt-4 bg-purple-900/20 p-4 rounded-lg border border-purple-800/30">
                  <h5 className="text-purple-400 font-medium mb-2">What this means for you:</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-2">
                    <li>Stream as much HD or 4K content as you want</li>
                    <li>Download large files and games without restrictions</li>
                    <li>No need to monitor your data usage</li>
                    <li>No unexpected charges for exceeding data limits</li>
                  </ul>
                </div>
              )}
              
              {feature.id === 'unshaped' && (
                <div className="mt-4 bg-blue-900/20 p-4 rounded-lg border border-blue-800/30">
                  <h5 className="text-blue-400 font-medium mb-2">What this means for you:</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-2">
                    <li>Enjoy smooth streaming even during peak hours</li>
                    <li>Experience consistent gaming performance</li>
                    <li>No degradation of video call quality</li>
                    <li>All your online activities receive equal priority</li>
                  </ul>
                </div>
              )}
              
              {feature.id === 'unthrottled' && (
                <div className="mt-4 bg-green-900/20 p-4 rounded-lg border border-green-800/30">
                  <h5 className="text-green-400 font-medium mb-2">What this means for you:</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-2">
                    <li>Consistent speeds throughout the month</li>
                    <li>No slowdowns after reaching usage thresholds</li>
                    <li>Reliable performance for work and entertainment</li>
                    <li>The speed you pay for is the speed you get - always</li>
                  </ul>
                </div>
              )}
              
              {feature.id === 'unlimited' && (
                <div className="mt-4 bg-pink-900/20 p-4 rounded-lg border border-pink-800/30">
                  <h5 className="text-pink-400 font-medium mb-2">What this means for you:</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-2">
                    <li>Connect as many devices as you want</li>
                    <li>Use any online service without restrictions</li>
                    <li>No fair usage policies limiting your activities</li>
                    <li>Freedom to use your internet connection your way</li>
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ISPFeatureItem;